import type { PostTypeSummaryDto, VehicleTypeSummaryDto } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { firstVehicleIndex, requirementColumns } from '@/lib/policyPreview';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE REQUIREMENT GRID, AS A CP NODAL WILL SEE IT IN P3.
 *
 *  This is the point of the policy builder. Someone defining a policy has no other way
 *  to tell whether they built the right thing, and a wrong policy propagates into every
 *  arrangement that uses it.
 *
 *  ⚠️ COMMAND ROLES RENDER OUTSIDE THE TABLE — as a SIBLING of `<table>`, never a row in
 *  it. FR-5.2.6b closes OD-9 precisely because everybody assumes command counts toward
 *  force, and the screen that defines the columns is where saying otherwise costs least.
 *
 *  A hand-written semantic `<table>`, NOT AntD's `Table`: there is no sorting, paging,
 *  selection or loading state here, so AntD's is all cost — and its nested
 *  `colgroup`/measure-row DOM makes `getAllByRole('columnheader')` return more than the
 *  visible columns, which is exactly the assertion exit criterion 10 rests on.
 *
 *  It lives in `components/` rather than beside the builder because it has three
 *  containers: the builder's sticky column, the publish confirmation, and `/design`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type RequirementPreviewProps = {
  postTypeIds: readonly string[];
  postTypes: ReadonlyMap<string, PostTypeSummaryDto>;
  vehicleTypeIds: readonly string[];
  vehicleTypes: ReadonlyMap<string, VehicleTypeSummaryDto>;
  /** Label and required-flag only. They are NOT part of the column derivation. */
  commandRoles: readonly { label: string; isRequired: boolean }[];
  className?: string;
};

export const RequirementPreview = ({
  postTypeIds,
  postTypes,
  vehicleTypeIds,
  vehicleTypes,
  commandRoles,
  className,
}: RequirementPreviewProps) => {
  const columns = requirementColumns({ postTypeIds, postTypes, vehicleTypeIds, vehicleTypes });
  const vehicleStart = firstVehicleIndex(columns);
  const postCount = vehicleStart === -1 ? columns.length : vehicleStart;
  const vehicleCount = columns.length - postCount;

  return (
    <section
      className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}
      data-testid="requirement-preview"
    >
      <header className="border-b border-border px-3 py-2">
        <h3 className="text-sm font-semibold tracking-tight">Requirement grid</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">As a CP nodal will see it when raising an arrangement.</p>
      </header>

      {columns.length === 0 ? (
        <div className="p-3">
          {/* No CTA: the control is six inches to the left. */}
          <EmptyState size="sm" title="Add a post column and the grid appears here." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <caption className="sr-only">
              {`Requirement columns: ${String(postCount)} post type(s) and ${String(vehicleCount)} vehicle type(s).`}
            </caption>
            <thead>
              <tr data-testid="requirement-preview-groups">
                <th scope="col" className="w-24" />
                <th
                  colSpan={postCount}
                  scope="colgroup"
                  className="px-1 py-1 text-[10px] font-medium uppercase tracking-wide text-subtle"
                >
                  Force
                </th>
                {vehicleCount > 0 && (
                  <th
                    colSpan={vehicleCount}
                    scope="colgroup"
                    className="border-l border-border px-1 py-1 text-[10px] font-medium uppercase tracking-wide text-subtle"
                  >
                    Vehicles
                  </th>
                )}
              </tr>
              <tr data-testid="requirement-preview-columns" className="border-b border-border">
                <th scope="col" className="sr-only">
                  Source
                </th>
                {columns.map((column, index) => (
                  <th
                    key={column.id}
                    scope="col"
                    title={column.fullLabel}
                    className={cn(
                      'max-w-20 truncate px-1 py-1.5 text-center font-medium',
                      index === vehicleStart && 'border-l border-border',
                    )}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/*
                Em-dashes, not numbers. This previews the COLUMN SET, not data — filling
                it with plausible figures would misrepresent what is being configured.
              */}
              <tr>
                <th scope="row" className="px-2 py-1.5 text-left font-medium text-muted-foreground">
                  Total required
                </th>
                {columns.map((column, index) => (
                  <td
                    key={column.id}
                    className={cn(
                      'px-1 py-1.5 text-center text-subtle',
                      index === vehicleStart && 'border-l border-border',
                    )}
                  >
                    —
                  </td>
                ))}
              </tr>
              <tr className="border-t border-dashed border-border">
                <th scope="row" className="px-2 py-1.5 text-left font-normal text-subtle">
                  Division · unit
                </th>
                {columns.map((column, index) => (
                  <td
                    key={column.id}
                    className={cn(
                      'px-1 py-1.5 text-center text-subtle',
                      index === vehicleStart && 'border-l border-border',
                    )}
                  >
                    —
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <p
        className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground"
        data-testid="requirement-preview-count"
      >
        {`${String(columns.length)} column${columns.length === 1 ? '' : 's'}`}
      </p>

      {/* ─── OUTSIDE THE GRID. A sibling of <table>, deliberately, not a row in it. ─── */}
      <section
        className="border-t border-border px-3 py-2"
        data-testid="requirement-preview-commands"
        aria-label="Command roles"
      >
        <h4 className="text-xs font-semibold tracking-tight">Command — outside the grid</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Command roles are never counted in a total, an allocation or a shortfall (FR-5.2.6b).
        </p>
        {commandRoles.length === 0 ? (
          <p className="mt-1.5 text-xs text-subtle">No command roles on this policy.</p>
        ) : (
          <ul className="mt-1.5 flex flex-col gap-0.5">
            {commandRoles.map((role) => (
              <li key={role.label} className="text-xs">
                {role.label}
                {role.isRequired && <span className="text-subtle"> · required</span>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
};
